import { useMemo } from "react";
import { useWatch, type Control } from "react-hook-form";
import { isVisibleFasteningType, type DeckDesignerResult } from "../../../http/deckDesigner/formulas";
import { L_SHAPE, RECTANGULAR_SHAPE } from "../../../http/deckDesigner/types/configurations";
import { SUPPORT_FAMILY_CODE_GRANULO } from "../../../http/deckDesigner/types/supportFamilies";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { createBottomViewLayout, createConnectorPoints, getJoistWidthMeters } from "./deckBottomViewGeometry";
import { getBoardSegments } from "./deckGeometry";
import { getFootprintCoordinates } from "./deckOutline";

export const useDeckBottomViewGeometry = (
  result: DeckDesignerResult | null,
  control: Control<DeckDesignerConfigurationFormValues>,
) => {
  const shape = useWatch({ control, name: "shape", exact: true });
  const base = useWatch({ control, name: "base", exact: true });
  const height = useWatch({ control, name: "height", exact: true });
  const secondaryBase = useWatch({ control, name: "secondaryBase", exact: true });
  const secondaryHeight = useWatch({ control, name: "secondaryHeight", exact: true });
  const lShapeCutoutCorner = useWatch({ control, name: "lShapeCutoutCorner", exact: true });
  const hasHorizontalBoards = useWatch({ control, name: "hasHorizontalBoards", exact: true });
  const boardWidth = useWatch({ control, name: "boardWidth", exact: true });
  const longDifferentBoardWidth = useWatch({ control, name: "longDifferentBoardWidth", exact: true });
  const shortDifferentBoardWidth = useWatch({ control, name: "shortDifferentBoardWidth", exact: true });
  const longDifferentBoardPosition = useWatch({ control, name: "longDifferentBoardPosition", exact: true });
  const shortDifferentBoardPosition = useWatch({ control, name: "shortDifferentBoardPosition", exact: true });
  const gap = useWatch({ control, name: "gap", exact: true });
  const substructure = useWatch({ control, name: "substructure", exact: true });

  const selectedShape = shape === RECTANGULAR_SHAPE ? RECTANGULAR_SHAPE : L_SHAPE;
  const supportFamilyCode = result?.support?.supportFamily.code;
  // A stripe-type GRANULO (e.g. GRANULO100/ROLL/MAT110) is a continuous roll/mat, not discrete point
  // supports like GRANULOPAD or NAG - so it has no support points to render in the bottom view.
  const isStripeSupport = supportFamilyCode === SUPPORT_FAMILY_CODE_GRANULO && result?.support?.stripeLength != null;
  const joistWidthMeters = getJoistWidthMeters(substructure?.base);

  const coordinates = useMemo(
    () =>
      getFootprintCoordinates({
        shape: selectedShape,
        base,
        height,
        secondaryBase,
        secondaryHeight,
        lShapeCutoutCorner,
      }),
    [base, height, lShapeCutoutCorner, secondaryBase, secondaryHeight, selectedShape],
  );

  const boardSegments = useMemo(
    () =>
      getBoardSegments(
        coordinates,
        selectedShape,
        boardWidth,
        gap,
        hasHorizontalBoards,
        result?.boardQuantity ?? null,
        longDifferentBoardWidth,
        shortDifferentBoardWidth,
        longDifferentBoardPosition,
        shortDifferentBoardPosition,
      ),
    [
      boardWidth,
      coordinates,
      longDifferentBoardWidth,
      longDifferentBoardPosition,
      gap,
      hasHorizontalBoards,
      result?.boardQuantity,
      shortDifferentBoardWidth,
      shortDifferentBoardPosition,
      selectedShape,
    ],
  );

  const bottomLayout = useMemo(
    () =>
      createBottomViewLayout(
        coordinates,
        selectedShape,
        hasHorizontalBoards,
        result?.substructureQuantity ?? null,
        result?.supportQuantity ?? null,
        supportFamilyCode,
        isStripeSupport,
      ),
    [
      coordinates,
      hasHorizontalBoards,
      result?.substructureQuantity,
      result?.supportQuantity,
      selectedShape,
      supportFamilyCode,
      isStripeSupport,
    ],
  );

  const connectorPoints = useMemo(
    () =>
      createConnectorPoints(
        boardSegments,
        bottomLayout.joists,
        hasHorizontalBoards,
        gap,
        !!result?.fasteningType && isVisibleFasteningType(result.fasteningType),
      ),
    [boardSegments, bottomLayout.joists, hasHorizontalBoards, gap, result?.fasteningType],
  );

  return {
    boardSegments,
    bottomLayout,
    connectorPoints,
    coordinates,
    isStripeSupport,
    joistWidthMeters,
    selectedShape,
    supportFamilyCode,
  };
};
